import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { companyApi } from '@/api';
import {
  Badge,
  BoxAvatar,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LinkButton,
  LoadingState,
  Modal,
  Pagination,
  SearchInput,
  SegmentedTabs,
  Select,
  useToast,
} from '@/components/ui';
import { isCandidateRestricted } from '@/features/applications/accountStanding';
import { CandidateRestrictionBadge } from '@/features/applications/CandidateRestriction';
import { APPLICATION_LABELS } from '@/features/applications/status';
import { DocumentViewer } from '@/features/verification/DocumentViewer';
import { errorMessage } from '@/hooks/useApiError';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';
import { formatDate } from '@/utils/format';

const PAGE_SIZE = 10;

// const TABS = [
//   { key: '', label: 'All' },
//   { key: 'SUBMITTED', label: 'New' },
//   { key: 'UNDER_REVIEW', label: 'In review' },
//   { key: 'SHORTLISTED', label: 'Shortlisted' },
//   { key: 'INTERVIEW', label: 'Interview' },
//   { key: 'OFFERED', label: 'Offered' },
//   { key: 'HIRED', label: 'Hired' },
//   { key: 'REJECTED', label: 'Rejected' },
// ];

/** Everyone, then three stages. Every status sits in one, so nobody is lost. */
const TABS = [
  { key: 'all', label: 'All', statuses: null },
  { key: 'review', label: 'In review', statuses: ['SUBMITTED', 'UNDER_REVIEW', 'SHORTLISTED'] },
  // { key: 'interview', label: 'Interview', statuses: ['INTERVIEW', 'DECISION_PENDING', 'OFFERED'] },
  // {
  //   key: 'decided',
  //   label: 'Accepted / rejected',
  //   statuses: ['OFFER_ACCEPTED', 'HIRED', 'REJECTED', 'WITHDRAWN'],
  // },
  // In review until an interview, and decided from the offer on, or at any
  // point the application is rejected.
  { key: 'interview', label: 'Interview', statuses: ['INTERVIEW', 'DECISION_PENDING'] },
  {
    key: 'decided',
    label: 'Accepted / rejected',
    statuses: ['OFFERED', 'OFFER_ACCEPTED', 'HIRED', 'REJECTED', 'WITHDRAWN'],
  },
];

const tabCount = (tab, counts) =>
  (tab.statuses ?? Object.keys(counts)).reduce((total, status) => total + (counts[status] ?? 0), 0);

/** The colour of the match bar, by how strong the fit is. */
function matchTone(score) {
  if (score >= 75) return 'bg-success-500';
  if (score >= 50) return 'bg-brand-500';
  return 'bg-warning-500';
}

/** Moves that need a permission of their own; the API refuses them otherwise. */
const MOVE_PERMISSIONS = {
  SHORTLISTED: PERMISSIONS.CANDIDATE_SHORTLIST,
  REJECTED: PERMISSIONS.CANDIDATE_REJECT,
};

/** "Now" once the date has passed, as it only means they are free already. */
function availability(date) {
  if (!date) return 'Not given';
  const start = new Date(date);
  if (start <= new Date()) return 'Now';
  return start.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

/**
 * Everyone who applied to one internship, best match first: who they are,
 * where they study, when they can start, and a status to change on the spot.
 * They can be narrowed by status or name and reordered by date. The full
 * application is one click away.
 */
export function ApplicationsReceived({ internshipId }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = usePermissions();
  const [page, setPage] = useState(1);
  const [tabKey, setTabKey] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('match');
  const [rejecting, setRejecting] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [viewingCv, setViewingCv] = useState(null);

  // const params = { page, page_size: PAGE_SIZE, internship_id: internshipId, sort: 'match' };
  // The same internship and search as the list, so a tab's count is its rows.
  const countParams = {
    internship_id: internshipId,
    ...(search.trim() ? { q: search.trim() } : {}),
  };
  const { data: counts } = useQuery({
    queryKey: ['company-application-counts', countParams],
    queryFn: () => companyApi.getApplicationCounts(countParams),
    placeholderData: keepPreviousData,
  });
  const activeTab = TABS.find((tab) => tab.key === tabKey);

  const params = {
    page,
    page_size: PAGE_SIZE,
    internship_id: internshipId,
    sort,
    ...(activeTab.statuses ? { statuses: activeTab.statuses } : {}),
    ...(search.trim() ? { q: search.trim() } : {}),
  };
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['company-applications', params],
    queryFn: () => companyApi.listApplications(params),
    placeholderData: keepPreviousData,
  });

  const tabs = TABS.map((tab) => ({
    ...tab,
    count: counts ? tabCount(tab, counts) : undefined,
  }));
  const searching = search.trim() !== '';
  // "Top match" means of everyone, not of one stage or one search.
  const filtered = searching || activeTab.statuses !== null;

  const move = useMutation({
    mutationFn: ({ id, ...input }) => companyApi.changeApplicationStatus(id, input),
    onSuccess: () => {
      toast.success('Status updated');
      setRejecting(null);
      void queryClient.invalidateQueries({ queryKey: ['company-applications'] });
      void queryClient.invalidateQueries({ queryKey: ['company-application-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['company-internship', internshipId] });
      void queryClient.invalidateQueries({ queryKey: ['company-internships'] });
      void queryClient.invalidateQueries({ queryKey: ['company-internship-counts'] });
    },
    onError: (moveError) => toast.error(errorMessage(moveError, 'Could not change the status')),
  });

  function choose(row, status) {
    if (status === 'REJECTED') {
      setRejectionReason('');
      setRejecting(row);
      return;
    }
    move.mutate({ id: row.id, status });
  }

  // The "new applications" button links here, past the listing's header.
  const { hash } = useLocation();
  const section = useRef(null);
  useEffect(() => {
    if (hash === '#applications-received' && !isPending) section.current?.scrollIntoView?.();
  }, [hash, isPending]);

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <section ref={section} aria-labelledby="applications-received" className="mb-6 scroll-mt-20">
      <h2 id="applications-received" className="text-ink mb-3 text-xl font-semibold tracking-tight">
        Applications Received
      </h2>

      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <SegmentedTabs
          // variant="plain"
          variant="pills"
          label="Application status"
          tabs={tabs}
          value={activeTab.key}
          onChange={(key) => {
            setTabKey(key);
            setPage(1);
          }}
        />
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <Select
            label="Order"
            hideLabel
            size="sm"
            wrapperClassName="w-full sm:w-36"
            value={sort}
            onChange={(event) => {
              setSort(event.target.value);
              setPage(1);
            }}
          >
            <option value="match">Best fit</option>
            <option value="recent">Most recent</option>
          </Select>
          <SearchInput
            label="Search by name"
            hideLabel
            size="sm"
            className="w-full sm:w-56"
            placeholder="Search by name"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            onClear={() => {
              setSearch('');
              setPage(1);
            }}
          />
        </div>
      </div>

      {isError ? (
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      ) : isPending ? (
        <LoadingState label="Loading applications…" />
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            compact
            title={
              searching
                ? 'Nobody matches this search'
                : activeTab.statuses
                  ? 'Nobody at this stage'
                  : 'Nobody has applied yet'
            }
          />
        </Card>
      ) : (
        <ul className="space-y-3">
          {rows.map((row, index) => {
            // Only a best-fit order has ranks; rows with no score are unranked.
            const rank =
              sort === 'match' && row.match_score != null
                ? (page - 1) * PAGE_SIZE + index + 1
                : null;
            // A suspended candidate's application is kept but frozen.
            const moves = isCandidateRestricted(row)
              ? []
              : (row.allowed_transitions ?? []).filter(
                  (status) =>
                    can(PERMISSIONS.APPLICATION_UPDATE) &&
                    (!MOVE_PERMISSIONS[status] || can(MOVE_PERMISSIONS[status])),
                );
            return (
              <li key={row.id}>
                {/* <Card> */}
                <Card
                  className={`transition hover:-translate-y-0.5 hover:shadow-md ${
                    rank === 1 && !filtered ? 'ring-success-500 ring-2' : ''
                  }`}
                >
                  <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
                    <div className="flex min-w-0 items-start gap-3">
                      <BoxAvatar name={row.student.full_name} round />
                      <div className="min-w-0 flex-1">
                        <p className="text-ink flex flex-wrap items-center gap-2 font-semibold">
                          {rank != null && (
                            <span className="text-ink-muted tabular-nums">#{rank}</span>
                          )}
                          {row.student.full_name}
                          {rank === 1 && !filtered && <Badge tone="success">Top match</Badge>}
                          {/* Applications made before matching existed have no
                            score, and none is invented for them. */}
                          {row.match_score != null && (
                            <Badge tone="brand">{Math.round(row.match_score)}% match</Badge>
                          )}
                          <CandidateRestrictionBadge row={row} />
                        </p>
                        <p className="text-ink-muted mt-1 text-sm">
                          {row.student.degree ?? row.student.headline ?? 'No degree added yet'}
                        </p>
                        {row.match_score != null && (
                          <div
                            aria-hidden="true"
                            className="bg-surface-muted mt-2 h-1.5 max-w-64 overflow-hidden rounded-full"
                          >
                            <div
                              className={`${matchTone(row.match_score)} h-full rounded-full`}
                              style={{ width: `${Math.min(row.match_score, 100)}%` }}
                            />
                          </div>
                        )}
                        <p className="text-ink-muted mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                          <span className="inline-flex items-center gap-1">
                            <Icon name="calendar" size={12} />
                            Applied {formatDate(row.created_at)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Icon name="fileText" size={12} />
                            {row.has_resume ? 'CV attached' : 'No CV'}
                          </span>
                        </p>
                      </div>
                    </div>
                    <Detail label="University" value={row.student.university ?? 'Not given'} />
                    <Detail label="Availability" value={availability(row.student.available_from)} />
                  </div>

                  <div className="border-line mt-4 flex flex-wrap items-end justify-between gap-3 border-t pt-4">
                    <Select
                      label={`Status of ${row.student.full_name}`}
                      hideLabel
                      size="sm"
                      wrapperClassName="w-full sm:w-44"
                      value={row.status}
                      disabled={moves.length === 0 || move.isPending}
                      onChange={(event) => choose(row, event.target.value)}
                    >
                      {[row.status, ...moves].map((status) => (
                        <option key={status} value={status}>
                          {APPLICATION_LABELS[status] ?? status}
                        </option>
                      ))}
                    </Select>
                    <div className="flex flex-wrap items-center gap-2">
                      {row.has_resume && (
                        <Button variant="secondary" size="sm" onClick={() => setViewingCv(row)}>
                          <Icon name="fileText" size={14} />
                          View CV
                        </Button>
                      )}
                      <LinkButton
                        to={paths.company.applicationDetail(row.id)}
                        variant="secondary"
                        size="sm"
                        title={`View ${row.student.full_name}'s application`}
                      >
                        View Details
                      </LinkButton>
                    </div>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {meta && meta.total_pages > 1 && (
        <Pagination
          page={meta.page}
          pageSize={PAGE_SIZE}
          totalPages={meta.total_pages}
          totalItems={meta.total_items}
          onPageChange={setPage}
        />
      )}

      {viewingCv && (
        <DocumentViewer
          open
          onClose={() => setViewingCv(null)}
          title={`${viewingCv.student.full_name} — CV`}
          fileName={viewingCv.resume_file_name}
          queryKey={['application-resume', viewingCv.id]}
          // Only the full application carries the short-lived signed link.
          loadUrl={() => companyApi.getApplication(viewingCv.id).then((fresh) => fresh.resume)}
          canDownload={false}
        />
      )}

      <Modal
        open={Boolean(rejecting)}
        title="Reject application"
        description="The reason is shown to the applicant, so keep it constructive. Any booked round is cancelled and any open offer withdrawn."
        onClose={() => setRejecting(null)}
      >
        <div className="space-y-4">
          <Input
            label="Reason"
            placeholder="Looking for more backend experience"
            value={rejectionReason}
            onChange={(event) => setRejectionReason(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRejecting(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              isLoading={move.isPending}
              onClick={() =>
                move.mutate({
                  id: rejecting.id,
                  status: 'REJECTED',
                  rejection_reason: rejectionReason || undefined,
                })
              }
            >
              Reject
            </Button>
          </div>
        </div>
      </Modal>
    </section>
  );
}

function Detail({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-ink text-sm font-semibold">{label}</p>
      <p className="text-ink-muted mt-1 text-sm">{value}</p>
    </div>
  );
}
